/*
 * WHICH LISTS ONE VIEW IS READING, and the setter that changes it.
 *
 * The client half of decisions 501 and 502: the scope is a MODE — `all`,
 * `primary` or `selected` — and `all` and `primary` are resolved server-side on
 * every read, so the list ids travel beside the mode rather than being derivable
 * here. A client holding only the mode could not say which lists it is looking
 * at, and its own answer would go stale the moment a list is created or the
 * primary flag moves.
 *
 * NOT OPTIMISTIC, which is the one way this differs from `useGroupBy` and the
 * difference is the server's role. A regroup is client-side work over data
 * already in hand, so that toggle moves first and the PUT follows. Here the
 * server decides which rows exist: a control that moved first would state a
 * scope the rows on screen are not showing, and on a failed write it would go on
 * stating it. So the write lands, the response is applied, and the view refetches
 * against the same scope the label now reads.
 *
 * ONE READ PER INSTANCE AND NO CACHE. The endpoint answers all four views in one
 * response and three views mounting is three cheap reads; a context or a module
 * cache would be a second source of truth for a value the server recomputes on
 * every read, and it would be the stale one exactly when a list has just been
 * created.
 *
 * NOT ON A CLOCK, either. Nothing changes a view's scope except this control, so
 * the read happens on mount and after a write. Active's five-minute wake refresh
 * re-reads the PAYLOAD (384) and deliberately does not come through here.
 *
 * GATED ON THE SAME TWO CONDITIONS THE CONTROL IS, which is the opposite choice
 * from `useMultiList` and for a stated reason: there the COUNT is one of the two
 * conditions and cannot be known without reading, so that hook reads
 * unconditionally. Here both conditions are already in hand, so an account that
 * cannot see the control makes no view-scope request at all.
 */

import { useCallback, useEffect, useState } from "react";

import { api, list, type ViewListScope, type ViewName } from "./api";

export interface ScopeChoice {
  scope: ViewListScope;
  /** The chosen lists, for `selected` alone. Refused by the server otherwise. */
  listIDs?: string[];
}

export interface ViewScope {
  /**
   * The stored mode, or null until the read lands -- and null forever when this
   * hook is disabled or its read failed. Null is what the control renders
   * nothing on: a failed read costs the control and nothing else, exactly as
   * `useMultiList`'s failed list read does, because the view underneath is
   * already showing the scope the server resolved for it.
   */
  scope: ViewListScope | null;

  /**
   * What the mode resolves to right now. Always at least one list when `scope`
   * is set (502's fallback), so the panel never has to render an empty scope.
   */
  listIDs: string[];

  /** True while a write is in flight, so the panel can stop taking presses. */
  saving: boolean;

  /** Set after a write that did not land. Cleared by the next attempt. */
  failed: boolean;

  setScope: (next: ScopeChoice) => void;
}

export function useViewScope(
  view: ViewName,
  enabled: boolean,
  onChanged: () => void,
): ViewScope {
  const [scope, setScope] = useState<ViewListScope | null>(null);
  const [listIDs, setListIDs] = useState<string[]>([]);
  const [saving, setSaving] = useState(false);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    if (!enabled) return;
    let cancelled = false;

    api
      .viewLists()
      .then((payload) => {
        if (cancelled) return;
        setScope(payload[view].scope);
        setListIDs(list(payload[view].list_ids));
      })
      .catch(() => {
        // Deliberately silent, and see `scope` above for why: there is nothing
        // here for a reader to act on, and the rows are unaffected.
      });

    return () => {
      cancelled = true;
    };
  }, [view, enabled]);

  const change = useCallback(
    (next: ScopeChoice) => {
      setSaving(true);
      setFailed(false);

      void api
        .setViewLists(view, {
          scope: next.scope,
          // `list_ids` belongs to `selected` alone and is refused with 422 under
          // the other two modes, which resolve without a stored set.
          ...(next.scope === "selected" ? { list_ids: next.listIDs ?? [] } : {}),
        })
        .then((payload) => {
          setScope(payload[view].scope);
          setListIDs(list(payload[view].list_ids));
          // THE VIEW REFETCHES, so the rows and the label cannot describe
          // different scopes. Called after the response is applied rather than
          // beside the request, so the refetch is always against the scope the
          // server confirmed.
          onChanged();
        })
        .catch(() => {
          // NOTHING LOCAL CHANGED, so there is nothing to revert -- the control
          // is still reading the scope the server last confirmed, which is the
          // scope the rows are showing. What a reader needs is the sentence.
          setFailed(true);
        })
        .finally(() => setSaving(false));
    },
    [view, onChanged],
  );

  return { scope, listIDs, saving, failed, setScope: change };
}
